import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AppConfigProvider } from './contexts/AppConfigContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import ProtectedRoute from './components/common/ProtectedRoute';
import Layout from './components/layout/Layout';

// Tailwind CSS Import
import './index.css';

// Legacy CSS Imports (temporarily commented out to allow new design system)
// import './styles/globals.css';
// import './styles/components.css';
// import './styles/themes.css';
// import './styles/layout.css';
// import './styles/sessions.css';
// import './styles/analytics.css';
// import './styles/admin.css';
// import './styles/students.css';
// import './styles/games.css';

// Auth Pages
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';

// Dashboard Pages
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';

// Question Pages
import QuestionBank from './pages/questions/QuestionBank.js';
import QuestionDetails from './pages/questions/QuestionDetails';
import CreateQuestion from './pages/questions/CreateQuestion.js';

// Test Pages
import TestLogin from './pages/tests/TestLogin';
import UserTestResults from './pages/tests/UserTestResults';
import TestResults from './pages/tests/TestResults';
import ComprehensiveInsights from './pages/tests/ComprehensiveInsights';
import PracticeTests from './pages/tests/PracticeTests';
import TakeTest from './pages/tests/TakeTest';
import TakePracticeTest from './pages/tests/TakePracticeTest';
import PracticeTestResults from './pages/tests/PracticeTestResults.jsx';
import ExamSettings from './pages/tests/ExamSettings';
import GamesPage from './pages/GamesPage';
import GamePlay from './components/Games/GamePlay';
import GameCreator from './components/Games/GameCreator';
import GameResults from './pages/games/GameResults';

// Analytics Pages
import Analytics from './pages/analytics/Analytics';
import Performance from './pages/analytics/Performance';
import Progress from './pages/analytics/Progress';
import Activity from './pages/analytics/Activity';

// Parent Pages
import ParentDashboard from './pages/parent/ParentDashboard';
import ChildProgress from './pages/parent/ChildProgress';
import ManageChildren from './pages/parent/ManageChildren';
import ChildrenProgress from './pages/parent/ChildrenProgress';
import ChildrenResults from './pages/parent/ChildrenResults';
import Insights from './pages/analytics/Insights';
import Messages from './pages/communications/Messages';
import Meetings from './pages/communications/Meetings';
import MeetingJoin from './pages/communications/MeetingJoin';
import MeetingDetail from './pages/communications/MeetingDetail';
import Notifications from './pages/communications/Notifications';

// Referral Pages
import ReferralDashboard from './pages/referral/ReferralDashboard';

// Admin Routes
import AdminDashboard from './pages/admin/AdminDashboard';
import UserManagement from './pages/admin/UserManagement';
import TeachersManagement from './pages/admin/TeachersManagement';
import StudentsManagement from './pages/admin/StudentsManagement';
import ParentsManagement from './pages/admin/ParentsManagement';
import StaffPage from './pages/admin/StaffPage';
import ContentManagement from './pages/admin/ContentManagement';
import SyllabusManagement from './pages/admin/SyllabusManagement';
import PastQuestions from './pages/admin/PastQuestions.jsx';
import AIQuestionGeneration from './pages/admin/AIQuestionGeneration';
import SystemSettings from './pages/admin/SystemSettings';
import ReferralManagement from './pages/admin/ReferralManagement';
import RewardManagement from './pages/admin/RewardManagement';
import GamesManagement from './pages/admin/GamesManagement';
import AirtimeTransactions from './pages/admin/AirtimeTransactions';

// Students
import Students from './pages/Students';
import NewStudent from './pages/NewStudent';
import StudentProgress from './pages/students/StudentProgress';
import StudentsProgress from './pages/students/StudentsProgress';

// Error Pages
import NotFound from './pages/errors/NotFound';
import Unauthorized from './pages/errors/Unauthorized';
import ServerError from './pages/errors/ServerError';

// Session routes removed: all tele-sessions are under /meetings
const RedirectToMeetings = () => <Navigate to="/meetings" replace />;
const RedirectToMeetingDetail = () => {
  const { id } = useParams();
  return <Navigate to={id ? `/meetings/${id}` : '/meetings'} replace />;
};
const RedirectToMeetingJoin = () => {
  const { id } = useParams();
  return <Navigate to={id ? `/meetings/${id}/join` : '/meetings'} replace />;
};

function App() {
  return (
    <ThemeProvider>
        <NotificationProvider>
          <AppConfigProvider>
          <AuthProvider>
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <div className="App">
              <Routes>
                {/* Public Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/test-login" element={<TestLogin />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />
                <Route path="/unauthorized" element={<Unauthorized />} />
                <Route path="/server-error" element={<ServerError />} />

                {/* Protected Routes */}
                <Route path="/" element={
                  <ProtectedRoute>
                    <Layout>
                      <Dashboard />
                    </Layout>
                  </ProtectedRoute>
                } />

                {/* Dashboard Routes */}
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <Layout>
                      <Dashboard />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/profile" element={
                  <ProtectedRoute>
                    <Layout>
                      <Profile />
                    </Layout>
                  </ProtectedRoute>
                } />

                {/* Question Routes */}
                <Route path="/questions" element={
                  <ProtectedRoute>
                    <Layout>
                      <QuestionBank />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/questions/:id" element={
                  <ProtectedRoute>
                    <Layout>
                      <QuestionDetails />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/questions/create" element={
                  <ProtectedRoute allowedRoles={['teacher', 'admin']}>
                    <Layout>
                      <CreateQuestion />
                    </Layout>
                  </ProtectedRoute>
                } />

                {/* Test Routes */}
                <Route path="/test-results" element={
                  <ProtectedRoute>
                    <Layout>
                      <UserTestResults />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/comprehensive-insights" element={
                  <ProtectedRoute>
                    <Layout>
                      <ComprehensiveInsights />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/practice-tests" element={
                  <ProtectedRoute>
                    <Layout>
                      <PracticeTests />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/exam-settings" element={
                  <ProtectedRoute allowedRoles={['teacher', 'admin']}>
                    <Layout>
                      <ExamSettings />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/tests/:id/take" element={
                  <ProtectedRoute>
                    <TakeTest />
                  </ProtectedRoute>
                } />

                <Route path="/practice-test/take" element={
                  <ProtectedRoute>
                    <Layout>
                      <TakePracticeTest />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/tests/:id/results" element={
                  <ProtectedRoute>
                    <Layout>
                      <TestResults />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/practice-tests/:id/results" element={
                  <ProtectedRoute>
                    <Layout>
                      <PracticeTestResults />
                    </Layout>
                  </ProtectedRoute>
                } />

                {/* Legacy session routes: everything is under /meetings (tele-sessions + meetings) */}
                <Route path="/sessions" element={<ProtectedRoute><RedirectToMeetings /></ProtectedRoute>} />
                <Route path="/sessions/history" element={<ProtectedRoute><RedirectToMeetings /></ProtectedRoute>} />
                <Route path="/sessions/create" element={<ProtectedRoute><RedirectToMeetings /></ProtectedRoute>} />
                <Route path="/sessions/:id" element={<ProtectedRoute><RedirectToMeetingDetail /></ProtectedRoute>} />
                <Route path="/sessions/:id/join" element={<ProtectedRoute><RedirectToMeetingJoin /></ProtectedRoute>} />

                {/* Game Routes */}
                <Route path="/games" element={
                  <ProtectedRoute>
                    <Layout>
                      <GamesPage />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/games/create" element={
                  <ProtectedRoute allowedRoles={['teacher', 'admin']}>
                    <Layout>
                      <GameCreator />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/games/:gameId/play" element={
                  <ProtectedRoute allowedRoles={['student', 'parent']}>
                    <GamePlay />
                  </ProtectedRoute>
                } />

                <Route path="/games/play/:gameId" element={
                  <ProtectedRoute allowedRoles={['student', 'parent']}>
                    <GamePlay />
                  </ProtectedRoute>
                } />

                <Route path="/games/:gameId/results" element={
                  <ProtectedRoute allowedRoles={['student']}>
                    <Layout>
                      <GameResults />
                    </Layout>
                  </ProtectedRoute>
                } />

                {/* Analytics Routes */}
                <Route path="/analytics" element={
                  <ProtectedRoute>
                    <Layout>
                      <Analytics />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/analytics/performance" element={
                  <ProtectedRoute>
                    <Layout>
                      <Performance />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/analytics/activity" element={
                  <ProtectedRoute>
                    <Layout>
                      <Activity />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/analytics/progress" element={
                  <ProtectedRoute>
                    <Layout>
                      <Progress />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/students/progress" element={
                  <ProtectedRoute allowedRoles={['teacher', 'admin']}>
                    <Layout>
                      <StudentsProgress />
                    </Layout>
                  </ProtectedRoute>
                } />

                {/* Legacy routes for backward compatibility */}
                <Route path="/performance" element={
                  <ProtectedRoute>
                    <Layout>
                      <Performance />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/progress" element={
                  <ProtectedRoute>
                    <Layout>
                      <Progress />
                    </Layout>
                  </ProtectedRoute>
                } />

                {/* Parent Routes */}
                <Route path="/parent" element={
                  <ProtectedRoute allowedRoles={['parent']}>
                    <Layout>
                      <ParentDashboard />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/parent/children" element={
                  <ProtectedRoute allowedRoles={['parent']}>
                    <Layout>
                      <ManageChildren />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/parent/child/:childId/progress" element={
                  <ProtectedRoute allowedRoles={['parent']}>
                    <Layout>
                      <ChildProgress />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/children/progress" element={
                  <ProtectedRoute allowedRoles={['parent']}>
                    <Layout>
                      <ChildrenProgress />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/children/results" element={
                  <ProtectedRoute allowedRoles={['parent']}>
                    <Layout>
                      <ChildrenResults />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/children/sessions" element={<ProtectedRoute><RedirectToMeetings /></ProtectedRoute>} />

                <Route path="/analytics/insights" element={
                  <ProtectedRoute allowedRoles={['parent']}>
                    <Layout>
                      <Insights />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/messages" element={
                  <ProtectedRoute allowedRoles={['parent','admin','teacher']}>
                    <Layout>
                      <Messages />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/meetings" element={
                  <ProtectedRoute allowedRoles={['parent','admin','teacher','student']}>
                    <Layout>
                      <Meetings />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/meetings/:meetingId/join" element={
                  <ProtectedRoute allowedRoles={['parent','admin','teacher','student']}>
                    <MeetingJoin />
                  </ProtectedRoute>
                } />
                <Route path="/meetings/:meetingId" element={
                  <ProtectedRoute allowedRoles={['parent','admin','teacher','student']}>
                    <Layout>
                      <MeetingDetail />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/notifications" element={
                  <ProtectedRoute allowedRoles={['parent','admin','teacher']}>
                    <Layout>
                      <Notifications />
                    </Layout>
                  </ProtectedRoute>
                } />

                {/* Referral Routes */}
                <Route path="/referrals" element={
                  <ProtectedRoute>
                    <Layout>
                      <ReferralDashboard />
                    </Layout>
                  </ProtectedRoute>
                } />

                {/* Admin Routes */}
                <Route path="/admin" element={
                  <ProtectedRoute allowedRoles={['admin', 'staff']}>
                    <Layout>
                      <AdminDashboard />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/admin/analytics" element={
                  <ProtectedRoute allowedRoles={['admin', 'staff']}>
                    <Layout>
                      <Analytics />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/admin/performance" element={
                  <ProtectedRoute allowedRoles={['admin', 'staff']}>
                    <Layout>
                      <Performance />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/admin/activity" element={
                  <ProtectedRoute allowedRoles={['admin', 'staff']}>
                    <Layout>
                      <Activity />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/admin/progress" element={
                  <ProtectedRoute allowedRoles={['admin', 'staff']}>
                    <Layout>
                      <Progress />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/admin/users" element={
                  <ProtectedRoute allowedRoles={['admin', 'staff']}>
                    <Layout>
                      <UserManagement />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/admin/teachers" element={
                  <ProtectedRoute allowedRoles={['admin', 'staff']}>
                    <Layout>
                      <TeachersManagement />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/admin/students" element={
                  <ProtectedRoute allowedRoles={['admin', 'staff']}>
                    <Layout>
                      <StudentsManagement />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/admin/parents" element={
                  <ProtectedRoute allowedRoles={['admin', 'staff']}>
                    <Layout>
                      <ParentsManagement />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/admin/staff" element={
                  <ProtectedRoute allowedRoles={['admin', 'staff']}>
                    <Layout>
                      <StaffPage />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/students" element={
                  <ProtectedRoute allowedRoles={['teacher', 'admin']}>
                    <Layout>
                      <Students />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/students/new" element={
                  <ProtectedRoute allowedRoles={['admin', 'staff']}>
                    <Layout>
                      <NewStudent />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/students/progress/:studentId" element={
                  <ProtectedRoute allowedRoles={['teacher', 'admin']}>
                    <Layout>
                      <StudentProgress />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/admin/content" element={
                  <ProtectedRoute allowedRoles={['teacher', 'admin']}>
                    <Layout>
                      <ContentManagement />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/admin/syllabus" element={
                  <ProtectedRoute allowedRoles={['teacher', 'admin']}>
                    <Layout>
                      <SyllabusManagement />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/admin/past-questions" element={
                  <ProtectedRoute allowedRoles={['admin', 'staff']}>
                    <Layout>
                      <PastQuestions />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/admin/ai-questions" element={
                  <ProtectedRoute allowedRoles={['admin', 'staff']}>
                    <Layout>
                      <AIQuestionGeneration />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/admin/games" element={
                  <ProtectedRoute allowedRoles={['admin', 'staff']}>
                    <Layout>
                      <GamesManagement />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/admin/settings" element={
                  <ProtectedRoute allowedRoles={['admin', 'staff']}>
                    <Layout>
                      <SystemSettings />
                    </Layout>
                  </ProtectedRoute>
                } />

                {/* Additional System Settings deep-link routes */}
                <Route path="/admin/security" element={
                  <ProtectedRoute allowedRoles={['admin', 'staff']}>
                    <Layout>
                      <SystemSettings />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/admin/database" element={
                  <ProtectedRoute allowedRoles={['admin', 'staff']}>
                    <Layout>
                      <SystemSettings />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/admin/api" element={
                  <ProtectedRoute allowedRoles={['admin', 'staff']}>
                    <Layout>
                      <SystemSettings />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/jitsi" element={
                  <ProtectedRoute allowedRoles={['admin', 'staff']}>
                    <Layout>
                      <SystemSettings />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/admin/referrals" element={
                  <ProtectedRoute allowedRoles={['admin', 'staff']}>
                    <Layout>
                      <ReferralManagement />
                    </Layout>
                  </ProtectedRoute>
                } />

                {/* Admin Referral Deep-link Routes */}
                <Route path="/admin/referrals/users" element={
                  <ProtectedRoute allowedRoles={['admin', 'staff']}>
                    <Layout>
                      <ReferralManagement />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/admin/referrals/stats" element={
                  <ProtectedRoute allowedRoles={['admin', 'staff']}>
                    <Layout>
                      <ReferralManagement />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/admin/referrals/qr" element={
                  <ProtectedRoute allowedRoles={['admin', 'staff']}>
                    <Layout>
                      <ReferralManagement />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/admin/rewards" element={
                  <ProtectedRoute allowedRoles={['admin', 'staff']}>
                    <Layout>
                      <RewardManagement />
                    </Layout>
                  </ProtectedRoute>
                } />

                {/* Admin Rewards Deep-link Routes */}
                <Route path="/admin/rewards/history" element={
                  <ProtectedRoute allowedRoles={['admin', 'staff']}>
                    <Layout>
                      <RewardManagement />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/rewards/analytics" element={
                  <ProtectedRoute allowedRoles={['admin', 'staff']}>
                    <Layout>
                      <RewardManagement />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/rewards/settings" element={
                  <ProtectedRoute allowedRoles={['admin', 'staff']}>
                    <Layout>
                      <RewardManagement />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/admin/airtime/transactions" element={
                  <ProtectedRoute allowedRoles={['admin', 'staff']}>
                    <Layout>
                      <AirtimeTransactions />
                    </Layout>
                  </ProtectedRoute>
                } />

                {/* Error Routes */}
                <Route path="/404" element={<NotFound />} />
                <Route path="/500" element={<ServerError />} />
                
                {/* Catch all route */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </div>
          </Router>
        </AuthProvider>
          </AppConfigProvider>
      </NotificationProvider>
    </ThemeProvider>
  );
}

export default App;
